import React from 'react';
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  Box,
  Toolbar,
  Stack,
  StackProps,
  useTheme,
  useMediaQuery,
  TypographyProps,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useLocation } from 'react-router-dom';
import HomeHeader from '../Home/components/HomeHeader';
import { TP_COLORS } from '../../constants';
import HomeFooter from '../Home/components/HomeFooter';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../components/BreadCrumb/Breadcrumbs';
import papersDataCambridgeIgcse from '../../constants/PastPapers/CambridgeIgcse';
import papersDataEdexcelIgcse from '../../constants/PastPapers/EdexcelIgcse';
import papersDataEdexcelGcse from '../../constants/PastPapers/EdexcelGcse';
import papersDataAqaGcse from '../../constants/PastPapers/AqaGcse';
import papersDataCambridgeAlevel from '../../constants/PastPapers/CambridgeAlevel';
import { getUrlConvertedString } from '../../helper';
import papersDataEdexcelAlevel from '../../constants/PastPapers/EdexcelAlevel';
import papersDataEdexcelInternationalAlevel from '../../constants/PastPapers/EdexcelInternationalAlevel';
import Faq from './Faq';

const curriculumTypoProps: TypographyProps = {
  fontWeight: '600',
  sx: {
    background: 'linear-gradient(90deg, #01B8AF 0%, #00615D 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
};

const topStackProps: StackProps['sx'] = {
  direction: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  style: { padding: '30px 0' },
};

const yearContainerProps: TypographyProps['sx'] = {
  backgroundColor: TP_COLORS.blue.medium,
  color: '#fff',
  textAlign: 'center',
  padding: 1,
  fontWeight: 'bold',
  borderTopLeftRadius: 4,
  borderTopRightRadius: 4,
};

const constructBreadcrumbOptions = (
  curriculum: string | undefined,
  subject: string | undefined,
  subjectCode: string,
  subjectLevel: string | undefined
) => {
  const curriculumName = getUrlConvertedString(`${curriculum}`);
  const subjectName = getUrlConvertedString(`${subject}`);
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
    {
      type: 'link',
      title: `${curriculum}`,
      link: `${process.env.PUBLIC_URL}/${curriculumName}`,
    },
    {
      type: 'link',
      title: `${subject}`,
      link: `${process.env.PUBLIC_URL}/dashboard/${curriculumName}/${subjectName}/${subjectLevel}/${subjectCode}`,
    },
    {
      type: 'text',
      title: 'Past Papers',
      highlight: true,
    },
  ];

  return options;
};

const FeatureCard = ({ title, description, imageSrc, bgColor, titleColor }) => {
  return (
    <Card
      style={{ backgroundColor: bgColor, height: '100%', borderRadius: 10 }}
      elevation={0}
    >
      <CardContent>
        <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
          <img src={imageSrc} alt={title} style={{ width: 35, height: 35 }} />
        </Box>
        <Typography
          sx={{ fontWeight: 'bold', fontSize: '1.05rem', color: titleColor }}
          gutterBottom
          align="center"
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', pt: 1 }} align="center">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

interface LocationState {
  subjectCode: string;
  subjectLevel: string;
  curriculum: string;
  subject: string;
}

const PastPapersPage: React.FC = () => {
  const location = useLocation();
  const state = location.state as LocationState;
  const subjectCode = state?.subjectCode;
  const subjectLevel = state?.subjectLevel;
  const curriculum = state?.curriculum;
  const subject = state?.subject;
  const breadcrumbOptions = constructBreadcrumbOptions(
    curriculum,
    subject,
    subjectCode,
    subjectLevel
  );
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  let papersData;
  const paperSubject = getUrlConvertedString(subject).replace(/[-()]/g, '');
  if (curriculum === 'Cambridge IGCSE') {
    papersData = papersDataCambridgeIgcse[paperSubject];
  } else if (curriculum === 'Edexcel IGCSE') {
    papersData = papersDataEdexcelIgcse[paperSubject];
  } else if (curriculum === 'Edexcel GCSE') {
    papersData = papersDataEdexcelGcse[paperSubject];
  } else if (curriculum === 'AQA GCSE') {
    papersData = papersDataAqaGcse[paperSubject];
  } else if (curriculum === 'Cambridge International A Levels') {
    papersData = papersDataCambridgeAlevel[paperSubject];
  } else if (curriculum === 'Edexcel A Levels') {
    papersData = papersDataEdexcelAlevel[paperSubject];
  } else if (curriculum === 'Edexcel International A Levels') {
    papersData = papersDataEdexcelInternationalAlevel[paperSubject];
  } else {
    papersData = [];
  }

  return (
    <>
      <HomeHeader />
      <Toolbar />
      <Container maxWidth="lg" sx={{ mt: '1%', mb: '4%' }}>
        <Stack {...topStackProps}>
          <Breadcrumbs
            options={breadcrumbOptions}
            textColor={TP_COLORS.gray.light}
            showDropDown={belowMd}
            highlightColor="#3498DB"
          />
        </Stack>
        <Typography
          variant="h5"
          align="center"
          gutterBottom
          style={{
            marginTop: '10px',
            fontWeight: 'bold',
            marginBottom: '20px',
          }}
        >
          <span style={{ color: TP_COLORS.blue.dark }}>
            {curriculum} Past Papers Practice: Essential for Exam Success
          </span>
        </Typography>
        <Typography
          variant="body1"
          align="center"
          gutterBottom
          style={{ marginBottom: '40px', fontSize: '15px' }}
        >
          Practice makes perfect, and our curated collection of IGCSE past
          papers will help you master every subject. Whether it’s Mathematics,
          Physics, Chemistry, Biology, or English, you’ll find IGCSE Exam Papers
          designed to match the official syllabus.
        </Typography>

        {/* Grid of years and papers */}
        <Grid container spacing={3}>
          {papersData.map((yearData) => (
            <Grid item xs={12} sm={6} md={3} key={yearData.year}>
              <Card sx={{ borderRadius: 2 }} elevation={0}>
                <CardContent
                  sx={{
                    padding: 0,
                    backgroundColor: '#F8FAFC',
                  }}
                >
                  <Typography
                    variant="h6"
                    component="div"
                    sx={yearContainerProps}
                  >
                    {yearData.year}
                  </Typography>
                  <Grid
                    container
                    direction="column"
                    spacing={1}
                    sx={{
                      padding: '10px 15px 0 15px',
                    }}
                  >
                    {yearData.papers.map((paper, index) => (
                      <Grid item key={index}>
                        <a
                          href={paper.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: 'none',
                            color: 'inherit',
                            display: 'block',
                          }}
                        >
                          <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              padding: '5px',
                              transition: 'background-color 0.3s',
                              '&:hover': {
                                backgroundColor: '#D3D3D34A',
                              },
                            }}
                          >
                            <Typography variant="body1">
                              {paper.label}
                            </Typography>
                            <IconButton>
                              <OpenInNewIcon sx={{ fontSize: '20px' }} />
                            </IconButton>
                          </Grid>
                        </a>
                        {/* Display the marking scheme link */}
                        {paper.marking && (
                          <a
                            href={paper.marking}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              textDecoration: 'none',
                              color: 'inherit',
                              display: 'block',
                            }}
                          >
                            <Grid
                              container
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{
                                padding: '5px',
                                transition: 'background-color 0.3s',
                                '&:hover': {
                                  backgroundColor: '#D3D3D34A',
                                },
                              }}
                            >
                              <img
                                src={`${process.env.PUBLIC_URL}/images/pastPaperPage/arrowright.png`}
                                alt="rightarrow"
                                style={{ width: 15, height: 15 }}
                              />
                              <Typography
                                variant="body1"
                                sx={{ marginLeft: -4 }}
                              >
                                Marking Scheme
                              </Typography>
                              <IconButton>
                                <OpenInNewIcon sx={{ fontSize: '20px' }} />
                              </IconButton>
                            </Grid>
                          </a>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography
          variant="h5"
          align="center"
          gutterBottom
          style={{ marginTop: '50px', fontWeight: 'bold' }}
        >
          <span style={{ color: TP_COLORS.blue.dark }}>Why Use IGCSE</span>
          <span style={{ color: TP_COLORS.tutopiyaGreenMain.darken }}>
            {' '}
            Past Papers?
          </span>
        </Typography>
        <Grid
          container
          spacing={4}
          justifyContent="center"
          style={{ marginTop: '20px' }}
        >
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              title="Real Exam Practice"
              description="Familiarize yourself with actual IGCSE exam formats, question styles, and mark schemes."
              imageSrc={`${process.env.PUBLIC_URL}/images/pastPaperPage/exam.png`}
              bgColor="#3498DB1A"
              titleColor="#3498DB"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              title="All Major Subjects"
              description="Access past papers for IGCSE Maths, Sciences, Languages, and Humanities."
              imageSrc={`${process.env.PUBLIC_URL}/images/pastPaperPage/books.png`}
              bgColor="#22C55E1A"
              titleColor="#22C55E"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              title="Detailed Solutions"
              description="Benefit from expert solutions that help you learn from mistakes and improve your exam strategy."
              imageSrc={`${process.env.PUBLIC_URL}/images/pastPaperPage/tips.png`}
              bgColor="#FFAB001A"
              titleColor="#FFAB00"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              title="Time Management Skills"
              description="Practice under timed conditions to perfect your time management during exams."
              imageSrc={`${process.env.PUBLIC_URL}/images/pastPaperPage/timer.png`}
              bgColor="#7A41001A"
              titleColor="#7A4100"
            />
          </Grid>
        </Grid>
        {(curriculum === 'Cambridge IGCSE' ||
          curriculum === 'Edexcel IGCSE') && (
          <>
            <Typography
              variant="h5"
              align="center"
              gutterBottom
              style={{
                marginTop: '50px',
                fontWeight: 'bold',
                marginBottom: '50px',
              }}
            >
              <span style={{ color: TP_COLORS.blue.dark }}>
                We are here to answer all your{' '}
              </span>
              <br />
              <Typography
                variant="h5"
                component="span"
                {...curriculumTypoProps}
              >
                {curriculum} Past Papers
              </Typography>
              <span style={{ color: TP_COLORS.blue.dark }}> questions</span>
            </Typography>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Faq />
            </div>
          </>
        )}
      </Container>
      <div style={{ height: '15px' }} />
      <Box
        sx={{
          background: `url('${process.env.PUBLIC_URL}/images/shapes/wave-with-gadgets.png') bottom left/contain repeat-x`,
          height: { xs: '100px', sm: '186px' },
        }}
      />
      <HomeFooter />
    </>
  );
};

export default PastPapersPage;
